/**
 * RoyaleLobbyRoster
 *
 * The waiting-room body once a lobby exists: live roster (scrolls for up to
 * 100), a 15-min expiry countdown, and the host's Start control. Start is
 * enabled at MIN_TO_START joined players and auto-fires at AUTO_START_AT (the
 * display cap) — the parent owns the actual start dispatch; this is pure UI +
 * the enable/auto-fire signalling.
 *
 * Presentational: the parent passes players, whether I'm the host, the
 * expiry timestamp, and callbacks. No store/socket access here.
 */

import React, {useEffect, useMemo, useState} from 'react';
import {Pressable, ScrollView, StyleSheet, View} from 'react-native';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';

import CCText from '@/Atoms/CCText';
import CCColors from '@/Utils/CCColors';
import CCTextInput from '@/Atoms/CCTextInput';
import {FontSizes, vs} from '@/Utils/Dimensions';
// Shared app pager (Previous / 1 2 3 / Next) — same control the tournaments
// tab, video courses and meetings use.
import Pagination from '@/Screens/Games/Pagination';

import {
  AUTO_START_AT,
  MIN_TO_START,
} from '@/Screens/FunChallenges/royaleConstants';
import type {RoyaleLobbyPlayer} from '@/Screens/FunChallenges/royaleTypes';
import {buildColorMap} from '@/Screens/FunChallenges/util/royaleColors';

const TEAL_DARK = '#1F6C63';
const GREEN = '#1ECF52';
const TEXT_DARK = '#262322';
const TEXT_MID = '#4D3F37';
const TEXT_MUTED = '#8A7E5F';
const YELLOW = '#FACF47';
const YELLOW_DARK = '#A9873B';
const BORDER = 'rgba(38, 35, 34, 0.10)';
/** Roster rows per page — the app-wide convention. */
const ROSTER_PAGE_SIZE = 10;

interface Props {
  players: RoyaleLobbyPlayer[];
  myUserId: string;
  isHost: boolean;
  /** Epoch ms the lobby expires. */
  expiresAt: number;
  /** Now in ms (parent ticks this so the countdown re-renders). */
  nowMs: number;
  /** Host pressed Start. */
  onStart: () => void;
  /** Start has been dispatched; disable the button until the server responds. */
  starting?: boolean;
  /**
   * Fired ONCE when the roster reaches AUTO_START_AT (parent gates classroom
   * mode, where auto-start is disabled, before dispatching).
   */
  onAutoStartReady?: () => void;

  /* ── Tournament reuse (the scheduled-tournament lobby renders this SAME
     component so the two waiting rooms are visually identical). Every field
     below is optional and defaults to the ad-hoc-lobby behaviour, so existing
     callers are untouched. ───────────────────────────────────────────────── */
  /**
   * Countdown semantics. 'expiry' (default) = the lobby self-destructs at
   * `expiresAt` ("Expires in 2:31" → "Lobby expired"). 'start' = `expiresAt`
   * holds the SCHEDULED START instead, so the pill counts down to kick-off
   * ("Starts in 4:12" → "Starting…") — a tournament doesn't expire, it begins.
   */
  countdownMode?: 'expiry' | 'start';
  /** Header label override (default "Players"). */
  title?: string;
  /**
   * Replaces the host Start button / "waiting for host" box entirely — the
   * tournament passes its Register/Withdraw control here, since registering is
   * the action in that waiting room, not starting.
   */
  footer?: React.ReactNode;
  /** Copy for the non-host waiting box (default "Waiting for the host to start…"). */
  waitingText?: string;
}

export default function RoyaleLobbyRoster({
  players,
  myUserId,
  isHost,
  expiresAt,
  nowMs,
  onStart,
  starting = false,
  onAutoStartReady,
  countdownMode = 'expiry',
  title = 'Players',
  footer,
  waitingText,
}: Props) {
  const count = players.length;
  // Can't start below the minimum, after expiry, or once a start is already
  // in flight (item 5: a 2nd click before the ACK could double-fire start).
  const canStart = count >= MIN_TO_START && !starting;
  // Colour by join order (the roster IS join order), "you" forced green.
  const colorOf = useMemo(
    () => buildColorMap(players.map(p => String(p.userId)), myUserId),
    [players, myUserId],
  );
  const remainingMs = Math.max(0, expiresAt - nowMs);
  const expired = remainingMs <= 0;

  const mmss = useMemo(() => {
    const s = Math.ceil(remainingMs / 1000);
    const mm = Math.floor(s / 60);
    const ss = String(s % 60).padStart(2, '0');
    return `${mm}:${ss}`;
  }, [remainingMs]);

  // Auto-start signal — fire once when we cross the cap. NEVER in 'start'
  // (tournament) mode: a tournament starts on its schedule, not on a full
  // roster, and the caller passes no start dispatcher there anyway.
  useEffect(() => {
    if (countdownMode === 'start') return;
    if (count >= AUTO_START_AT && onAutoStartReady) {
      onAutoStartReady();
    }
  }, [count, onAutoStartReady, countdownMode]);

  // Roster paging + search. A tournament roster runs to max_players (100 on
  // nearly every tournament), which overflowed the scroll box into an
  // unreadable column. Both controls hide below one page so the common small
  // ad-hoc lobby looks exactly as it did.
  //
  // NOTE the roster is LIVE here — players join and leave while this is open.
  // Paging is index-based, so a join on page 1 shifts everyone down; that is
  // the same behaviour as the in-race standings list and is preferable to
  // freezing the view. The clamp below covers the shrink case.
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return players;
    return players.filter(p =>
      (p.userId === myUserId ? 'you' : p.name || 'Player')
        .toLowerCase()
        .includes(q),
    );
  }, [players, query, myUserId]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / ROSTER_PAGE_SIZE));
  useEffect(() => {
    setPage(1);
  }, [query]);
  // A player LEAVING can shrink the list under the current page — clamp so the
  // roster never renders empty with rows still available.
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const pageRows = useMemo(
    () => filtered.slice((page - 1) * ROSTER_PAGE_SIZE, page * ROSTER_PAGE_SIZE),
    [filtered, page],
  );

  return (
    <View style={styles.root}>
      <View style={styles.header}>
        <CCText style={styles.headerTitle}>{`${title} · ${count}`}</CCText>
        <View
          style={[
            styles.timerPill,
            remainingMs < 60_000 && styles.timerPillUrgent,
          ]}>
          <CCText
            style={[
              styles.timerText,
              remainingMs < 60_000 && styles.timerTextUrgent,
            ]}>
            {countdownMode === 'start'
              ? expired
                ? 'Starting…'
                : `Starts in ${mmss}`
              : expired
              ? 'Lobby expired'
              : `Expires in ${mmss}`}
          </CCText>
        </View>
      </View>

      {players.length > ROSTER_PAGE_SIZE ? (
        <View style={styles.searchWrap}>
          <MaterialCommunityIcons
            name="magnify"
            size={16}
            color={TEXT_MUTED}
          />
          <CCTextInput
            value={query}
            onChangeText={setQuery}
            placeholder="Search player"
            placeholderTextColor={TEXT_MUTED}
            style={styles.searchInput}
            fontSize={13}
          />
          {query ? (
            <Pressable onPress={() => setQuery('')} hitSlop={8}>
              <MaterialCommunityIcons
                name="close-circle"
                size={16}
                color={TEXT_MUTED}
              />
            </Pressable>
          ) : null}
        </View>
      ) : null}

      <ScrollView
        style={styles.list}
        contentContainerStyle={styles.listContent}>
        {pageRows.length === 0 ? (
          <CCText style={styles.emptyRow}>
            {`No player matches "${query}".`}
          </CCText>
        ) : null}
        {pageRows.map(p => (
          <View key={p.userId} style={styles.playerRow}>
            <View
              style={[styles.dot, {backgroundColor: colorOf(p.userId)}]}>
              <CCText style={styles.dotText}>
                {(p.name || '?').charAt(0).toUpperCase()}
              </CCText>
            </View>
            <CCText
              style={[
                styles.playerName,
                p.userId === myUserId && styles.playerNameMe,
              ]}
              numberOfLines={1}>
              {p.userId === myUserId ? 'You' : p.name || 'Player'}
            </CCText>
            {p.isHost && (
              <View style={styles.hostBadge}>
                <CCText style={styles.hostBadgeText}>HOST</CCText>
              </View>
            )}
            <View style={styles.onlineDot} />
          </View>
        ))}
      </ScrollView>

      {totalPages > 1 ? (
        <View style={styles.pagerWrap}>
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            paginate={setPage}
            maxPage={7}
          />
        </View>
      ) : null}

      {/* A caller-supplied footer replaces the start/waiting controls entirely
          (the tournament puts Register/Withdraw here). */}
      {footer !== undefined ? (
        footer
      ) : isHost ? (
        <Pressable
          onPress={canStart && !expired ? onStart : undefined}
          disabled={!canStart || expired}
          style={({pressed}) => [
            styles.startBtn,
            (!canStart || expired) && styles.startBtnDisabled,
            canStart && !expired && pressed && styles.startBtnPressed,
          ]}>
          <CCText
            style={[
              styles.startBtnText,
              (!canStart || expired) && styles.startBtnTextDisabled,
            ]}>
            {expired
              ? 'Lobby expired'
              : starting
              ? 'Starting…'
              : canStart
              ? `Start Royale · ${count} player${count === 1 ? '' : 's'}`
              : `Need ${Math.max(0, MIN_TO_START - count)} more to start`}
          </CCText>
        </Pressable>
      ) : (
        <View style={styles.waitingBox}>
          <CCText style={styles.waitingText}>
            {waitingText
              ? waitingText
              : expired
              ? 'Lobby expired — ask the host to create a new one.'
              : 'Waiting for the host to start…'}
          </CCText>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  root: {gap: vs(12)},
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerTitle: {
    fontSize: FontSizes[13] || 13,
    fontWeight: '700',
    color: TEXT_MID,
    textTransform: 'uppercase',
    letterSpacing: 0.7,
  },
  timerPill: {
    paddingHorizontal: 12,
    paddingVertical: 5,
    borderRadius: 999,
    backgroundColor: 'rgba(61, 171, 158, 0.12)',
  },
  timerPillUrgent: {backgroundColor: 'rgba(225, 120, 70, 0.14)'},
  timerText: {
    fontSize: FontSizes[12] || 12,
    fontWeight: '700',
    color: TEAL_DARK,
  },
  timerTextUrgent: {color: '#E17846'},
  // Kept as a safety net rather than the primary bound: paging caps the list at
  // ROSTER_PAGE_SIZE rows, so this only matters if a row wraps taller than
  // expected. Without it a full page still fits without scrolling.
  list: {maxHeight: 360},
  listContent: {gap: vs(8)},
  searchWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    borderWidth: 1,
    borderColor: BORDER,
    borderRadius: 8,
    paddingHorizontal: 10,
    backgroundColor: CCColors.White,
    marginBottom: vs(8),
  },
  searchInput: {
    flex: 1,
    height: 36,
    borderWidth: 0,
    backgroundColor: 'transparent',
    paddingLeft: 0,
  },
  emptyRow: {
    fontSize: FontSizes[12],
    color: TEXT_MUTED,
    paddingVertical: vs(12),
    textAlign: 'center',
  },
  pagerWrap: {marginTop: vs(4)},
  playerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: BORDER,
    backgroundColor: CCColors.White,
  },
  dot: {
    width: 30,
    height: 30,
    borderRadius: 999,
    alignItems: 'center',
    justifyContent: 'center',
  },
  dotText: {color: CCColors.White, fontWeight: '800', fontSize: 12},
  playerName: {
    flex: 1,
    fontSize: FontSizes[14] || 14,
    fontWeight: '600',
    color: TEXT_DARK,
  },
  playerNameMe: {color: TEAL_DARK, fontWeight: '800'},
  hostBadge: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 5,
    backgroundColor: 'rgba(250, 207, 71, 0.22)',
  },
  hostBadgeText: {
    fontSize: 9,
    fontWeight: '800',
    color: YELLOW_DARK,
    letterSpacing: 0.5,
  },
  onlineDot: {
    width: 7,
    height: 7,
    borderRadius: 999,
    backgroundColor: GREEN,
  },
  startBtn: {
    marginTop: vs(4),
    backgroundColor: YELLOW,
    borderRadius: 14,
    borderBottomWidth: 4,
    borderColor: YELLOW_DARK,
    paddingVertical: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  startBtnPressed: {borderBottomWidth: 2, paddingVertical: 18},
  startBtnDisabled: {
    backgroundColor: '#F2E8C7',
    borderColor: '#D9C796',
  },
  startBtnText: {
    fontSize: FontSizes[15] || 15,
    fontWeight: '800',
    color: TEXT_DARK,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
  },
  startBtnTextDisabled: {color: TEXT_MUTED},
  waitingBox: {
    marginTop: vs(4),
    padding: 14,
    borderRadius: 12,
    backgroundColor: 'rgba(61, 171, 158, 0.10)',
    borderWidth: 1,
    borderColor: 'rgba(61, 171, 158, 0.25)',
  },
  waitingText: {
    fontSize: FontSizes[13] || 13,
    color: TEAL_DARK,
    fontWeight: '600',
    textAlign: 'center',
  },
});
